import { BlogPostPage } from '@denqfabrik/ghost-blog/pages';
import BlogListPage from '@/pages/BlogPage';
import { LandingProvider } from '@denqfabrik/landing-ui';
import { SentryErrorBoundary } from '@denqfabrik/sentry/client';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import brandConfig from '@/brand.config';
import Layout from '@/components/layout/Layout';
import { LanguageProvider, type SupportedLang, SUPPORTED_LANGS } from '@/i18n/LanguageProvider';
import { getRoutesForLang } from '@/i18n/routes';
import HomePage from '@/pages/HomePage';
import NotFoundPage from '@/pages/NotFoundPage';

const HowItWorksPage = lazy(() => import('@/pages/HowItWorksPage'));
const UseCasesPage = lazy(() => import('@/pages/UseCasesPage'));
const SecurityPage = lazy(() => import('@/pages/SecurityPage'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const EarlyAccessPage = lazy(() => import('@/pages/EarlyAccessPage'));
const ImprintPage = lazy(() => import('@/pages/ImprintPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const ThankYouPage = lazy(() => import('@/pages/ThankYouPage'));

const blogConfig = {
  baseUrl: 'https://aletiq.ai',
  siteName: 'Aletiq',
  langPrefix: 'all' as const,
  heroTitle: {
    en: 'Blog',
    de: 'Blog',
    it: 'Blog',
    es: 'Blog',
    fr: 'Blog',
  },
  heroSubtitle: {
    en: 'Insights on AI agent verification and quality assurance',
    de: 'Einblicke in KI-Agenten-Verifizierung und Qualit\u00e4tssicherung',
    it: 'Approfondimenti sulla verifica degli agenti IA e il controllo qualit\u00e0',
    es: 'Perspectivas sobre verificaci\u00f3n de agentes IA y control de calidad',
    fr: 'Perspectives sur la v\u00e9rification des agents IA et l\u2019assurance qualit\u00e9',
  },
  proseClassName: 'prose-blog',
};

/** Page components mapped to route keys */
const PAGES: Array<{ routeKey: string; Component: React.ComponentType }> = [
  { routeKey: 'how-it-works', Component: HowItWorksPage },
  { routeKey: 'use-cases', Component: UseCasesPage },
  { routeKey: 'security', Component: SecurityPage },
  { routeKey: 'pricing', Component: PricingPage },
  { routeKey: 'contact', Component: ContactPage },
  { routeKey: 'early-access', Component: EarlyAccessPage },
  { routeKey: 'imprint', Component: ImprintPage },
  { routeKey: 'privacy', Component: PrivacyPage },
  { routeKey: 'thank-you', Component: ThankYouPage },
];

/** Generate Route elements for a given language */
function langRoutes(lang: SupportedLang) {
  const routes = getRoutesForLang(lang);
  return [
    <Route key={`${lang}-home`} path={`/${lang}`} element={<HomePage />} />,
    ...PAGES.map(({ routeKey, Component }) => (
      <Route key={`${lang}-${routeKey}`} path={`/${lang}${routes[routeKey]}`} element={<Component />} />
    )),
    <Route key={`${lang}-blog`} path={`/${lang}/blog`} element={<BlogListPage config={blogConfig} />} />,
    <Route key={`${lang}-blog-post`} path={`/${lang}/blog/:slug`} element={<BlogPostPage config={blogConfig} />} />,
  ];
}

export default function App() {
  return (
    <LandingProvider config={brandConfig}>
      <LanguageProvider>
        <Layout>
          <Suspense fallback={<div />}>
            <SentryErrorBoundary fallback={({ error }) => {
              const err = error as Error | undefined;
              return (
                <div className="px-8 pt-32 text-center">
                  <h1 className="mb-4 text-2xl font-bold text-red-600">Something went wrong</h1>
                  {import.meta.env.DEV && (
                    <pre className="mx-auto max-w-2xl overflow-auto rounded bg-red-50 p-4 text-left text-sm text-red-800">
                      {err?.message ?? 'Unknown error'}
                      {'\n\n'}
                      {err?.stack ?? ''}
                    </pre>
                  )}
                </div>
              );
            }}>
              <Routes>
                {/* Root redirects to English (default) */}
                <Route path="/" element={<Navigate to="/en" replace />} />

                {/* All language routes */}
                {SUPPORTED_LANGS.map((lang) => langRoutes(lang))}

                {/* Legacy redirects (old unprefixed German routes) */}
                <Route path="/so-funktionierts" element={<Navigate to="/de/so-funktionierts" replace />} />
                <Route path="/anwendungsfaelle" element={<Navigate to="/de/anwendungsfaelle" replace />} />
                <Route path="/sicherheit" element={<Navigate to="/de/sicherheit" replace />} />
                <Route path="/pricing" element={<Navigate to="/en/pricing" replace />} />
                <Route path="/kontakt" element={<Navigate to="/de/kontakt" replace />} />
                <Route path="/impressum" element={<Navigate to="/de/impressum" replace />} />
                <Route path="/datenschutz" element={<Navigate to="/de/datenschutz" replace />} />
                <Route path="/blog" element={<Navigate to="/en/blog" replace />} />
                <Route path="/blog/:slug" element={<Navigate to="/en/blog" replace />} />
                <Route path="/architektur" element={<Navigate to="/de/sicherheit" replace />} />
                <Route path="/integrationen" element={<Navigate to="/de" replace />} />
                {/* Legacy thank-you redirects */}
                <Route path="/danke" element={<Navigate to="/de/danke" replace />} />
                <Route path="/thank-you" element={<Navigate to="/en/thank-you" replace />} />
                <Route path="/grazie" element={<Navigate to="/it/grazie" replace />} />
                <Route path="/gracias" element={<Navigate to="/es/gracias" replace />} />
                <Route path="/merci" element={<Navigate to="/fr/merci" replace />} />

                {/* 404 */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </SentryErrorBoundary>
          </Suspense>
        </Layout>
      </LanguageProvider>
    </LandingProvider>
  );
}
